import React from "react";
import { Button as MuiButton, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { brandColors, designTokens } from "~/presentation/themes/modernTheme";

const StyledButton = styled(MuiButton)(({ _, color, variant, size, fullWidth, loading }) => {
  const baseStyles = {
    borderRadius: designTokens.borderRadius.md,
    textTransform: "none",
    fontWeight: designTokens.typography.fontWeights.medium,
    fontFamily: designTokens.typography.fontFamily,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    boxShadow: "none",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      transition: "left 0.5s",
    },

    "&:hover::before": {
      left: "100%",
    },

    ...(loading && {
      pointerEvents: "none",
    }),
  };

  const sizeStyles = {
    small: {
      padding: `${designTokens.spacing.sm}px ${designTokens.spacing.md}px`,
      fontSize: "0.875rem",
      minHeight: 32,
    },
    medium: {
      padding: `${designTokens.spacing.md}px ${designTokens.spacing.lg}px`,
      fontSize: "0.875rem",
      minHeight: 40,
    },
    large: {
      padding: `${designTokens.spacing.lg}px ${designTokens.spacing.xl}px`,
      fontSize: "1rem",
      minHeight: 48,
    },
  };

  const variantStyles = {
    contained: {
      backgroundColor: brandColors[color][500],
      color: "white",
      "&:hover": {
        backgroundColor: brandColors[color][600],
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
        boxShadow: designTokens.shadows.sm,
      },
      "&:disabled": {
        backgroundColor: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
    outlined: {
      backgroundColor: "transparent",
      color: brandColors[color][500],
      border: `2px solid ${brandColors[color][500]}`,
      "&:hover": {
        backgroundColor: brandColors[color][50],
        borderColor: brandColors[color][600],
        color: brandColors[color][600],
        transform: "translateY(-1px)",
        boxShadow: designTokens.shadows.sm,
      },
      "&:active": {
        transform: "translateY(0)",
        backgroundColor: brandColors[color][100],
      },
      "&:disabled": {
        borderColor: brandColors.neutral[300],
        color: brandColors.neutral[400],
        backgroundColor: "transparent",
        transform: "none",
      },
    },
    text: {
      backgroundColor: "transparent",
      color: brandColors[color][500],
      border: "none",
      "&:hover": {
        backgroundColor: brandColors[color][50],
        color: brandColors[color][600],
      },
      "&:active": {
        backgroundColor: brandColors[color][100],
      },
      "&:disabled": {
        color: brandColors.neutral[400],
        backgroundColor: "transparent",
      },
    },
    gradient: {
      background: `linear-gradient(135deg, ${brandColors[color][500]} 0%, ${brandColors[color][700]} 100%)`,
      color: "white",
      border: "none",
      "&:hover": {
        background: `linear-gradient(135deg, ${brandColors[color][600]} 0%, ${brandColors[color][800]} 100%)`,
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        background: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
    secondary: {
      backgroundColor: brandColors.secondary[500],
      color: "white",
      "&:hover": {
        backgroundColor: brandColors.secondary[600],
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        backgroundColor: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
    success: {
      backgroundColor: brandColors.success[500],
      color: "white",
      "&:hover": {
        backgroundColor: brandColors.success[600],
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        backgroundColor: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
    warning: {
      backgroundColor: brandColors.warning[500],
      color: "white",
      "&:hover": {
        backgroundColor: brandColors.warning[600],
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        backgroundColor: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
    error: {
      backgroundColor: brandColors.error[500],
      color: "white",
      "&:hover": {
        backgroundColor: brandColors.error[600],
        boxShadow: designTokens.shadows.button,
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&:disabled": {
        backgroundColor: brandColors.neutral[300],
        color: brandColors.neutral[500],
        transform: "none",
        boxShadow: "none",
      },
    },
  };

  return {
    ...baseStyles,
    ...sizeStyles[size || "medium"],
    ...variantStyles[variant || "contained"],
    ...(fullWidth && { width: "100%" }),
  };
});

const ModernButton = ({
  children,
  variant = "contained",
  color = "primary",
  size = "medium",
  loading = false,
  loadingText = "Carregando...",
  startIcon,
  endIcon,
  fullWidth = false,
  disabled = false,
  onClick,
  className,
  sx,
  ...props
}) => {
  const buttonText = loading && loadingText ? loadingText : children;

  const variantColors = {
    primary: "primary",
    secondary: "secondary",
    success: "success",
    warning: "warning",
    error: "error",
  };

  return (
    <StyledButton
      color={variantColors[color] || "primary"}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      className={`modern-button ${className || ""}`}
      sx={sx}
      loadingPosition="start"
      {...props}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!loading && startIcon && (
          <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>{startIcon}</Box>
        )}
        <span>{buttonText}</span>
        {!loading && endIcon && (
          <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>{endIcon}</Box>
        )}
      </Box>
    </StyledButton>
  );
};

// Componente de grupo de botões
export const ModernButtonGroup = ({
  children,
  spacing = "sm",
  direction = "row",
  className,
  sx,
}) => {
  const spacingValue = designTokens.spacing[spacing] || designTokens.spacing.sm;

  return (
    <Box
      className={`modern-button-group ${className || ""}`}
      sx={{
        display: "flex",
        flexDirection: direction,
        gap: spacingValue,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Componente de botão flutuante
export const ModernFab = ({
  children,
  variant = "primary",
  size = "medium",
  position = "fixed",
  bottom = 24,
  right = 24,
  onClick,
  className,
  sx,
  ...props
}) => {
  const sizeStyles = {
    small: { width: 40, height: 40 },
    medium: { width: 56, height: 56 },
    large: { width: 72, height: 72 },
  };

  const variantColors = {
    primary: brandColors.primary[500],
    secondary: brandColors.secondary[500],
    success: brandColors.success[500],
    warning: brandColors.warning[500],
    error: brandColors.error[500],
  };

  return (
    <Box
      component="button"
      onClick={onClick}
      className={`modern-fab ${className || ""}`}
      sx={{
        position,
        bottom,
        right,
        ...sizeStyles[size],
        borderRadius: "50%",
        backgroundColor: variantColors[variant],
        color: "white",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: designTokens.shadows.lg,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 1000,
        "&:hover": {
          transform: "scale(1.1)",
          boxShadow: designTokens.shadows.xl,
        },
        "&:active": {
          transform: "scale(1.05)",
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default ModernButton;
